import { Form, Modal, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPublishAccount, deleteAgentBinding, deletePublishAccounts, getPublishAccountDetail, listAgentBindings, listAgents, searchPublishAccounts, updatePublishAccount, upsertAgentBinding, xhsMcpEnsure } from '../api';
import { useAuth } from '../auth/AuthContext';
import { useBillingQuota } from '../hooks/useBillingQuota';
import { fixMojibakeUtf8 } from '../utils/text';
import { resolveErrorMessage } from '../utils/errorMessage';
import { formatShanghaiDateTime } from '../utils/datetime';
import { CONTENT_DELIVERY_PLATFORM_OPTIONS } from '../utils/contentPlatforms';
import { getOperatorScopeId } from '../utils/operatorScope';
import SocialAccountLoginButton from '../components/SocialAccountLoginButton';
import AccountTablePanel from '../components/account-management/AccountTablePanel';
import AccountEditorDrawer from '../components/account-management/AccountEditorDrawer';
import AgentBindModal from '../components/account-management/AgentBindModal';
import { buildAccountColumns } from './account-management/columns';
import { buildHandleAccountSave } from './account-management/saveAccountFlow';
import {
  buildAccountSearchParams,
  createAccountDeleteHandlers,
  createAccountSearchHandlers,
  createLoadAccountDataHandler,
  createReloadCurrentAccountListHandler,
} from './account-management/accountQueryDeleteHandlers';
import {
  buildAgentSelectOptions,
  createCreateAgentBindingHandler,
  createCloseAgentBindModalHandler,
  createHandleAgentUnbindHandler,
  createLoadAgentBindingsHandler,
  createLoadAgentsHandler,
  createOpenAgentBindModalHandler,
  createSetPrimaryAgentBindingHandler,
  createSyncAccountLocalStateHandler,
  createDeleteAgentBindingHandler,
  createUpdateAgentBindingHandler,
} from './account-management/accountBindingHandlers';

const LOCAL_AGENT_PLATFORMS = new Set(['douyin', 'kuaishou', 'weixin_channels', 'wechat_channels']);

const AccountManagement = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const { user } = useAuth();

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [selectedAccountKeys, setSelectedAccountKeys] = useState([]);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create'); // create | edit
  const [detailLoading, setDetailLoading] = useState(false);
  const [xhsProvisioning, setXhsProvisioning] = useState(false);
  const [xhsProvisionAccount, setXhsProvisionAccount] = useState(null);
  const [socialProvisionAccount, setSocialProvisionAccount] = useState(null);
  const [agentList, setAgentList] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentBindVisible, setAgentBindVisible] = useState(false);
  const [agentBindSubmitting, setAgentBindSubmitting] = useState(false);
  const [agentBindTarget, setAgentBindTarget] = useState(null);
  const [agentSettingsActiveTab, setAgentSettingsActiveTab] = useState('binding');
  const [sessionLoginAgentId, setSessionLoginAgentId] = useState('');
  const [agentBindingsLoading, setAgentBindingsLoading] = useState(false);
  const [agentBindingItems, setAgentBindingItems] = useState([]);
  const [agentBindingPrimaryAgentId, setAgentBindingPrimaryAgentId] = useState(undefined);
  const [drawerAccount, setDrawerAccount] = useState(null);
  const [agentBindForm] = Form.useForm();
  const { quota, getHint, refreshQuota } = useBillingQuota();
  const operatorId = useMemo(() => getOperatorScopeId(), []);
  const paginationRef = useRef(pagination);
  const recentlyUpdatedAccountsRef = useRef(new Map());

  const resolveAccountCreateBlock = useCallback((quotaData) => {
    if (!quotaData?.plan) {
      return {
        blocked: true,
        message: '当前订阅未生效，请先购买或续费。',
      };
    }
    const remaining = quotaData?.remaining?.accounts;
    if (remaining !== null && remaining !== undefined && Number(remaining) <= 0) {
      return {
        blocked: true,
        message: getHint('accounts'),
      };
    }
    return { blocked: false, message: '' };
  }, [getHint]);

  const accountCreateBlock = useMemo(
    () => resolveAccountCreateBlock(quota),
    [quota, resolveAccountCreateBlock]
  );

  const accountQuotaTooltip = useMemo(() => (
    <span>
      {accountCreateBlock.message}
      <a href="/billing" style={{ marginLeft: 6 }}>去付费订阅</a>
    </span>
  ), [accountCreateBlock.message]);

  const redirectToBilling = useCallback(() => {
    navigate('/billing');
  }, [navigate]);

  const platformOptions = useMemo(() => CONTENT_DELIVERY_PLATFORM_OPTIONS, []);

  const statusMap = {
    active: { text: '活跃', color: 'green' },
    paused: { text: '已暂停', color: 'orange' },
    inactive: { text: '未激活', color: 'default' },
    suspended: { text: '已停用', color: 'red' },
  };

  const executionModeMap = {
    local_agent: { text: '本地 Agent', color: 'processing' },
    cloud_api: { text: '云端 API', color: 'blue' },
    cloud_browser: { text: '云端浏览器', color: 'purple' },
  };

  const formatDateTime = (dateTimeString) => {
    return formatShanghaiDateTime(dateTimeString);
  };

  const buildAgentReadinessView = useCallback((record) => {
    const readiness = record?.login_readiness && typeof record.login_readiness === 'object' ? record.login_readiness : {};
    const agentSummary = record?.agent_summary && typeof record.agent_summary === 'object' ? record.agent_summary : {};
    const platformCode = String(record?.platform_code || readiness?.platform_code || '').trim().toLowerCase();
    const executionMode = String(record?.execution_mode || readiness.execution_mode || '').trim();
    const agentRequired = readiness.agent_required === true
      || executionMode === 'local_agent'
      || LOCAL_AGENT_PLATFORMS.has(platformCode);
    const agentBound = readiness.agent_bound === true || Boolean(record?.agent_id);
    const hasOnlineFlag = typeof readiness.agent_online === 'boolean' || typeof agentSummary?.is_online === 'boolean';
    const agentOnline = typeof readiness.agent_online === 'boolean'
      ? readiness.agent_online
      : (typeof agentSummary?.is_online === 'boolean'
        ? agentSummary.is_online
        : String(agentSummary?.status || '').toLowerCase() === 'online');
    const canPublish = readiness.can_publish === true;
    const statusText = String(readiness.status_text || '').trim();
    const hostname = String(agentSummary?.hostname || '').trim();
    const heartbeatAt = String(agentSummary?.last_heartbeat_at || agentSummary?.last_seen_at || '').trim();

    if (!agentRequired) {
      return {
        tagText: '无需 Agent',
        tagColor: 'blue',
        summary: statusText || '当前账号走云端发布链路',
        hostname,
        heartbeatAt,
      };
    }
    if (!agentBound) {
      return {
        tagText: '未绑定',
        tagColor: 'orange',
        summary: statusText || '需要先绑定 Agent',
        hostname,
        heartbeatAt,
      };
    }
    if (hasOnlineFlag && !agentOnline) {
      return {
        tagText: '离线',
        tagColor: 'red',
        summary: statusText || '绑定 Agent 当前离线',
        hostname,
        heartbeatAt,
      };
    }
    if (canPublish) {
      return {
        tagText: '就绪',
        tagColor: 'green',
        summary: statusText || '可发布',
        hostname,
        heartbeatAt,
      };
    }
    return {
      tagText: '待登录',
      tagColor: 'gold',
      summary: statusText || '需要重新登录',
      hostname,
      heartbeatAt,
    };
  }, []);

  const formatListField = (value) => {
    if (value === null || value === undefined) return '';
    const items = Array.isArray(value) ? value : [value];
    const text = items
      .filter((item) => item !== null && item !== undefined)
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(', ');
    return text;
  };

  const parseCommaList = (value) => {
    if (value === null || value === undefined) return undefined;
    if (Array.isArray(value)) return value;
    const text = String(value).trim();
    if (!text) return undefined;
    return text
      .split(/[,，]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const normalizeOptionalString = useCallback((value) => {
    if (value === null || value === undefined) return undefined;
    const text = String(value);
    return text.trim() ? text.trim() : undefined;
  }, []);

  const parseJsonObject = (value) => {
    const text = normalizeOptionalString(value);
    if (!text) return undefined;
    try {
      const parsed = JSON.parse(text);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('platform_config 必须是 JSON object');
      }
      return parsed;
    } catch (error) {
      throw new Error(`platform_config JSON 解析失败：${error.message}`);
    }
  };

  const buildSearchParams = useCallback(
    (formValues = {}, page = 1, pageSize = 20) => buildAccountSearchParams({
      formValues,
      page,
      pageSize,
      normalizeOptionalString,
      operatorId,
    }),
    [normalizeOptionalString, operatorId]
  );

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const loadData = useCallback(async (searchParams = {}, page = null, pageSize = null) => {
    const run = createLoadAccountDataHandler({
      paginationRef,
      setLoading,
      searchPublishAccounts,
      recentlyUpdatedAccountsRef,
      setDataSource,
      setPagination,
      message,
      resolveErrorMessage,
    });
    return run(searchParams, page, pageSize);
  }, []);

  useEffect(() => {
    loadData({ operator_id: operatorId });
  }, [loadData, operatorId]);

  const loadAgents = useCallback(async ({ silent = false } = {}) => {
    const run = createLoadAgentsHandler({
      setAgentsLoading,
      listAgents,
      setAgentList,
      message,
    });
    return run({ silent });
  }, []);

  useEffect(() => {
    loadAgents({ silent: true });
  }, [loadAgents]);

  const loadAgentBindings = useCallback(async ({ silent = false } = {}) => {
    const run = createLoadAgentBindingsHandler({
      listAgentBindings,
      setAgentBindingsLoading,
      setAgentBindingItems,
      setAgentBindingPrimaryAgentId,
      agentBindTarget,
      operatorId,
      message,
    });
    return run({ silent });
  }, [agentBindTarget, operatorId]);

  const closeAgentBindModalBase = createCloseAgentBindModalHandler({
    setAgentBindVisible,
    setAgentBindTarget,
    setAgentBindingItems,
    setAgentBindingPrimaryAgentId,
    agentBindForm,
  });
  const openAgentBindModalBase = createOpenAgentBindModalHandler({
    setAgentBindTarget,
    agentBindForm,
    setAgentBindVisible,
    loadAgents,
    loadAgentBindings,
    message,
  });
  const closeAgentBindModal = () => {
    closeAgentBindModalBase();
    setAgentSettingsActiveTab('binding');
    setSessionLoginAgentId('');
  };
  const openAgentBindModal = (record) => {
    setAgentSettingsActiveTab('binding');
    setSessionLoginAgentId('');
    openAgentBindModalBase(record);
  };
  const syncAccountLocalState = useCallback((account) => {
    const run = createSyncAccountLocalStateHandler({
      normalizeOptionalString,
      setDataSource,
      recentlyUpdatedAccountsRef,
    });
    return run(account);
  }, [normalizeOptionalString]);
  const reloadCurrentAccountList = createReloadCurrentAccountListHandler({
    paginationRef,
    form,
    buildSearchParams,
    loadData,
  });

  const handleCreateBinding = createCreateAgentBindingHandler({
    message,
    upsertAgentBinding,
    syncAccountLocalState,
    drawerMode,
    drawerAccount,
    normalizeOptionalString,
    setDrawerAccount,
    detailForm,
    reloadCurrentAccountList,
    loadAgents,
    loadAgentBindings,
    setAgentBindSubmitting,
    agentBindForm,
    agentBindTarget,
    operatorId,
  });

  const handleUpdateBinding = createUpdateAgentBindingHandler({
    message,
    upsertAgentBinding,
    syncAccountLocalState,
    drawerMode,
    drawerAccount,
    normalizeOptionalString,
    setDrawerAccount,
    detailForm,
    reloadCurrentAccountList,
    loadAgentBindings,
    setAgentBindSubmitting,
    agentBindTarget,
    operatorId,
  });

  const handleDeleteBinding = createDeleteAgentBindingHandler({
    message,
    deleteAgentBinding,
    syncAccountLocalState,
    drawerMode,
    drawerAccount,
    normalizeOptionalString,
    setDrawerAccount,
    detailForm,
    reloadCurrentAccountList,
    loadAgentBindings,
    setAgentBindSubmitting,
    agentBindTarget,
    operatorId,
  });

  const handleSetPrimaryBinding = createSetPrimaryAgentBindingHandler({
    updateAgentBinding: handleUpdateBinding,
  });

  const handleAgentUnbind = createHandleAgentUnbindHandler({
    Modal,
    normalizeOptionalString,
    message,
    agentBindTarget,
    bindingPrimaryAgentId: agentBindingPrimaryAgentId,
    deleteAgentBinding: handleDeleteBinding,
    operatorId,
  });

  const agentSelectOptions = useMemo(() => buildAgentSelectOptions(agentList), [agentList]);

  const closeDetailDrawer = () => {
    setDrawerVisible(false);
    setXhsProvisioning(false);
    setXhsProvisionAccount(null);
    setDrawerAccount(null);
    detailForm.resetFields();
  };

  const fillDetailForm = (account = {}) => {
    detailForm.setFieldsValue({
      account_id: account.account_id,
      platform: account.platform,
      account_name: account.account_name,
      account_type: account.account_type,
      status: account.status,
      operator_user_ids: formatListField(account.operator_user_ids),
      operator_user_names: formatListField(account.operator_user_names),
      auth_config_id: account.auth_config_id,
      platform_config: account.platform_config ? JSON.stringify(account.platform_config, null, 2) : '',
    });
  };

  const { handleSearch, handleFilterFinish } = createAccountSearchHandlers({
    form,
    pagination,
    setSelectedAccountKeys,
    buildSearchParams,
    loadData,
  });

  const openCreateDrawer = () => {
    if (accountCreateBlock.blocked) {
      message.warning(`${accountCreateBlock.message} 正在前往付费订阅页面...`);
      redirectToBilling();
      return;
    }
    setDrawerMode('create');
    setDrawerVisible(true);
    setDetailLoading(false);
    setXhsProvisioning(false);
    setXhsProvisionAccount(null);
    setDrawerAccount(null);
    detailForm.resetFields();
    detailForm.setFieldsValue({
      status: 'active',
      login_after_create: true,
      operator_user_ids: user?.user_id ? String(user.user_id) : undefined,
      operator_user_names: user?.nickname ? String(fixMojibakeUtf8(user.nickname)) : undefined,
    });
  };

  const openEditDrawer = async (record) => {
    setDrawerMode('edit');
    setDrawerVisible(true);
    setDetailLoading(true);
    setDrawerAccount(null);
    detailForm.resetFields();

    try {
      const response = await getPublishAccountDetail({
        account_id: record.account_id,
        operator_id: operatorId,
      });
      if (response.status === 'success') {
        const account = response.account || record;
        setDrawerAccount(account);
        fillDetailForm(account);
      } else {
        message.error('账号不存在或已被删除');
        closeDetailDrawer();
      }
    } catch (error) {
      console.error('加载账号详情失败:', error);
      message.error('加载账号详情失败，请稍后重试');
      closeDetailDrawer();
    } finally {
      setDetailLoading(false);
    }
  };

  const { handleDelete, handleBatchDelete } = createAccountDeleteHandlers({
    selectedAccountKeys,
    dataSource,
    pagination,
    form,
    buildSearchParams,
    loadData,
    Modal,
    message,
    deletePublishAccounts,
    resolveErrorMessage,
    setSelectedAccountKeys,
  });

  const currentPageAccountIds = useMemo(
    () => (dataSource || []).map((item) => item.account_id).filter(Boolean),
    [dataSource]
  );

  const isAllCurrentPageSelected = useMemo(
    () => currentPageAccountIds.length > 0 && currentPageAccountIds.every((id) => (selectedAccountKeys || []).includes(id)),
    [currentPageAccountIds, selectedAccountKeys]
  );

  const toggleSelectAllCurrentPage = () => {
    if (currentPageAccountIds.length === 0) return;
    setSelectedAccountKeys((prevKeys) => {
      const otherPageSelected = (prevKeys || []).filter((key) => !currentPageAccountIds.includes(key));
      const prevAllCurrent = currentPageAccountIds.every((id) => (prevKeys || []).includes(id));
      if (prevAllCurrent) return otherPageSelected;
      return Array.from(new Set([...otherPageSelected, ...currentPageAccountIds]));
    });
  };

  const handleSave = buildHandleAccountSave({
    detailForm,
    drawerMode,
    normalizeOptionalString,
    parseCommaList,
    parseJsonObject,
    refreshQuota,
    resolveAccountCreateBlock,
    quota,
    message,
    redirectToBilling,
    createPublishAccount,
    updatePublishAccount,
    reloadCurrentAccountList,
    xhsMcpEnsure,
    setXhsProvisioning,
    setXhsProvisionAccount,
    setSocialProvisionAccount,
    upsertAgentBinding,
    agentList,
    operatorId,
    closeDetailDrawer,
    resolveErrorMessage,
    setDataSource,
    recentlyUpdatedAccountsRef,
  });

  const columns = buildAccountColumns({
    statusMap,
    formatListField,
    formatDateTime,
    executionModeMap,
    buildAgentReadinessView,
    operatorId,
    openAgentBindModal,
    reloadCurrentAccountList,
    openEditDrawer,
    handleDelete,
  });

  return (
    <div className="page-shell">
      <AccountTablePanel
        form={form}
        onFilterFinish={handleFilterFinish}
        onSearch={handleSearch}
        platformOptions={platformOptions}
        accountCreateBlock={accountCreateBlock}
        accountQuotaTooltip={accountQuotaTooltip}
        onCreate={openCreateDrawer}
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        selectedAccountKeys={selectedAccountKeys}
        onTableSelectionChange={(keys) => {
          setSelectedAccountKeys((prevKeys) => {
            const currentPageIds = (dataSource || []).map((item) => item.account_id);
            const otherPageSelected = (prevKeys || []).filter((key) => !currentPageIds.includes(key));
            const allSelectedKeys = [...otherPageSelected, ...(keys || [])];
            return Array.from(new Set(allSelectedKeys));
          });
        }}
        isAllCurrentPageSelected={isAllCurrentPageSelected}
        onToggleSelectAllCurrentPage={toggleSelectAllCurrentPage}
        onBatchDelete={handleBatchDelete}
        onClearSelection={() => setSelectedAccountKeys([])}
        pagination={pagination}
        onPageChange={(page, pageSize) => {
          const formValues = form.getFieldsValue();
          const searchParams = buildSearchParams(formValues, page, pageSize);
          loadData(searchParams, page, pageSize);
        }}
        onPageSizeChange={(_current, size) => {
          const formValues = form.getFieldsValue();
          const searchParams = buildSearchParams(formValues, 1, size);
          loadData(searchParams, 1, size);
        }}
      />

      <AccountEditorDrawer
        drawerMode={drawerMode}
        drawerVisible={drawerVisible}
        closeDetailDrawer={closeDetailDrawer}
        detailLoading={detailLoading}
        xhsProvisioning={xhsProvisioning}
        xhsProvisionAccount={xhsProvisionAccount}
        onXhsProvisionSuccess={() => {
          setXhsProvisionAccount(null);
          closeDetailDrawer();
          const formValues = form.getFieldsValue();
          const searchParams = buildSearchParams(formValues, pagination.current, pagination.pageSize);
          loadData(searchParams, pagination.current, pagination.pageSize);
        }}
        detailForm={detailForm}
        platformOptions={platformOptions}
        onSave={handleSave}
      />

      <AgentBindModal
        agentBindTarget={agentBindTarget}
        agentBindVisible={agentBindVisible}
        closeAgentBindModal={closeAgentBindModal}
        agentSettingsActiveTab={agentSettingsActiveTab}
        setAgentSettingsActiveTab={setAgentSettingsActiveTab}
        agentBindSubmitting={agentBindSubmitting}
        loadAgents={loadAgents}
        agentsLoading={agentsLoading}
        loadAgentBindings={loadAgentBindings}
        bindingsLoading={agentBindingsLoading}
        bindingItems={agentBindingItems}
        bindingPrimaryAgentId={agentBindingPrimaryAgentId}
        handleCreateBinding={handleCreateBinding}
        handleUpdateBinding={handleUpdateBinding}
        handleDeleteBinding={handleDeleteBinding}
        handleSetPrimaryBinding={handleSetPrimaryBinding}
        agentBindForm={agentBindForm}
        normalizeOptionalString={normalizeOptionalString}
        formatDateTime={formatDateTime}
        agentSelectOptions={agentSelectOptions}
        handleAgentUnbind={handleAgentUnbind}
        sessionLoginAgentId={sessionLoginAgentId}
        setSessionLoginAgentId={setSessionLoginAgentId}
        operatorId={operatorId}
        onLoginSuccess={async () => {
          await Promise.all([
            reloadCurrentAccountList(),
            loadAgentBindings({ silent: true }),
          ]);
        }}
      />

      {socialProvisionAccount && (
        <SocialAccountLoginButton
          account={socialProvisionAccount}
          operatorId={operatorId}
          autoStart
          buttonProps={{ style: { display: 'none' } }}
          onSuccess={() => {
            setSocialProvisionAccount(null);
            const formValues = form.getFieldsValue();
            const searchParams = buildSearchParams(formValues, pagination.current, pagination.pageSize);
            loadData(searchParams, pagination.current, pagination.pageSize);
          }}
        />
      )}
    </div>
  );
};

export default AccountManagement;
